<template>
    <div v-if="appStore.sidebarOpen" class="fixed inset-0 z-30 bg-sidebar/50 lg:hidden" @click="appStore.closeSidebar()" />

    <aside
        class="fixed inset-y-0 left-0 z-40 w-72 shrink-0 bg-sidebar text-sidebar-text flex flex-col h-screen px-4 py-5 transform transition-transform duration-200 lg:sticky lg:top-0 lg:z-auto lg:translate-x-0"
        :class="appStore.sidebarOpen ? 'translate-x-0' : '-translate-x-full'">
        <div class="flex items-center gap-3 px-1.5 pb-4">
            <img src="/branding/meltal-icon.png" alt="Meltal Group"
                class="h-11 w-11 shrink-0 rounded-[11px] ring-2 ring-primary/35 shadow-lg" />
            <div class="min-w-0">
                <p class="font-display font-semibold text-[17px] leading-tight text-sidebar-strong truncate">{{ $t('app.name') }}</p>
                <p class="text-[10.5px] uppercase tracking-[.09em] text-sidebar-subtle leading-tight">{{ $t('app.company') }}</p>
            </div>
            <UButton icon="i-lucide-x" variant="ghost" color="neutral" size="sm"
                class="ml-auto lg:hidden text-sidebar-text" @click="appStore.closeSidebar()" />
        </div>
        <div class="h-px mx-1.5 mb-3 bg-gradient-to-r from-primary/50 to-white/5" />

        <nav class="flex-1 overflow-y-auto -mx-1 px-1 flex flex-col gap-3">
            <div v-for="group in groups" :key="group.label" class="flex flex-col gap-0.5">
                <p class="px-3 pb-1 font-display text-[10.5px] uppercase tracking-[.1em] text-sidebar-muted">{{ group.label }}</p>

                <RouterLink v-for="item in group.items" :key="item.to.name" :to="item.to"
                    class="flex items-center gap-3 rounded-[9px] px-3 py-2.5 font-display text-[13.5px] font-medium uppercase tracking-[.03em] transition-colors"
                    :class="isActive(item.to.name)
                        ? 'bg-primary text-inverted shadow-[0_3px_10px] shadow-primary/25'
                        : 'text-sidebar-text hover:bg-white/[.07]'">
                    <UIcon :name="item.icon" class="size-[17px] shrink-0" />
                    <span class="truncate">{{ item.label }}</span>
                </RouterLink>
            </div>
        </nav>

        <a href="/navodila/XRT-navodila-za-uporabo.pdf" target="_blank" rel="noopener"
            class="mt-3 flex items-center gap-3 rounded-[9px] border border-primary/40 px-3 py-2.5 font-display text-[13.5px] font-medium uppercase tracking-[.03em] text-primary transition-colors hover:bg-primary/10">
            <UIcon name="i-lucide-book-open" class="size-[17px] shrink-0" />
            <span class="truncate">{{ $t('nav.guide') }}</span>
            <UIcon name="i-lucide-external-link" class="size-3.5 ml-auto opacity-60" />
        </a>

        <div class="mt-3 rounded-xl border border-white/10 bg-white/[.04] p-2.5 flex flex-col gap-2">
            <div class="flex items-center gap-2.5">
                <span class="flex items-center justify-center size-9 rounded-full bg-primary text-inverted font-display font-semibold text-sm shrink-0">
                    {{ initials }}
                </span>
                <div class="min-w-0 flex-1">
                    <p class="text-sm font-semibold leading-tight truncate text-sidebar-strong">{{ appStore.userData?.name }}</p>
                    <p class="text-xs leading-tight text-sidebar-subtle">{{ roleLabel }}</p>
                </div>
            </div>
            <div class="flex items-center gap-1.5">
                <UTooltip :text="themeLabel">
                    <UButton :icon="themeIcon" color="neutral" variant="ghost" size="sm" :aria-label="themeLabel"
                        class="text-sidebar-text hover:bg-white/10" @click="cycleTheme" />
                </UTooltip>
                <UButton :label="$t('dashboard.logout')" icon="i-lucide-log-out" color="neutral" variant="ghost" size="sm"
                    class="flex-1 justify-center text-sidebar-text hover:bg-white/10" @click="logout" />
            </div>
        </div>

        <div class="flex items-center gap-2 px-1.5 pt-3 text-[10.5px] text-sidebar-muted">
            <img src="/branding/meltal-icon.png" alt="" class="size-3.5 rounded-[3px] opacity-85" />
            <span>{{ $t('app.company') }}</span>
        </div>
    </aside>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import { navRouteName } from '@/libs/navIcons'
    import { useAppStore } from '@/stores/app'

    const { t } = useI18n()
    const router = useRouter()
    const route = useRoute()
    const appStore = useAppStore()

    const groups = computed(() => {
        const result = [
            {
                label: t('nav_groups.production'),
                items: [
                    { to: { name: 'dashboard' }, label: t('nav.dashboard'), icon: 'i-lucide-layout-dashboard' },
                    { to: { name: 'shifts' }, label: t('nav.shifts'), icon: 'i-lucide-clipboard-list' },
                    { to: { name: 'reports' }, label: t('nav.reports'), icon: 'i-lucide-bar-chart-3' },
                    { to: { name: 'electricity' }, label: t('nav.electricity'), icon: 'i-lucide-zap' },
                ],
            },
            {
                label: t('nav_groups.maintenance'),
                items: [
                    { to: { name: 'tickets' }, label: t('nav.tickets'), icon: 'i-lucide-flag' },
                    { to: { name: 'faults' }, label: t('nav.faults'), icon: 'i-lucide-alert-triangle' },
                    { to: { name: 'incidents' }, label: t('nav.incidents'), icon: 'i-lucide-wrench' },
                ],
            },
            {
                label: t('nav_groups.data'),
                items: [
                    { to: { name: 'personnel' }, label: t('nav.personnel'), icon: 'i-lucide-users' },
                    { to: { name: 'reference' }, label: t('nav.reference'), icon: 'i-lucide-list' },
                ],
            },
        ]

        if (appStore.isAdmin()) {
            result.push({
                label: t('nav_groups.admin'),
                items: [
                    { to: { name: 'schedule' }, label: t('nav.schedule'), icon: 'i-lucide-calendar-clock' },
                    { to: { name: 'field-bounds' }, label: t('nav.field_bounds'), icon: 'i-lucide-shield-alert' },
                    { to: { name: 'users' }, label: t('nav.users'), icon: 'i-lucide-user-cog' },
                ],
            })
        }

        return result
    })

    const themeOrder = ['auto', 'light', 'dark'] as const

    const themeIcon = computed(() => ({ auto: 'i-lucide-monitor', light: 'i-lucide-sun', dark: 'i-lucide-moon' })[appStore.colorMode as typeof themeOrder[number]] ?? 'i-lucide-monitor')
    const themeLabel = computed(() => t(`theme.${appStore.colorMode === 'auto' ? 'system' : appStore.colorMode}`))

    const cycleTheme = () => {
        const index = themeOrder.indexOf(appStore.colorMode as typeof themeOrder[number])
        appStore.colorMode = themeOrder[(index + 1) % themeOrder.length]!
    }

    const isActive = (name: string | symbol | undefined) => navRouteName(route.name) === name

    const roleLabel = computed(() => {
        switch (appStore.userData?.role) {
            case 'entry':  return t('users.role_entry')
            case 'admin':  return t('users.role_admin')
            case 'viewer': return t('users.role_viewer')
            default:       return ''
        }
    })

    const initials = computed(() => (appStore.userData?.name ?? '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase())
        .join(''))

    const logout = async () => {
        appStore.setLogout()
        await router.push({ name: 'login' })
    }
</script>
